import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  InputGroup,
  InputLeftElement,
  Flex,
  Text,
  TableContainer,
  VStack,
  Select,
  Button,
  useToast,
  Spinner,
  Badge,
  useBreakpointValue,
  FormControl,
  FormLabel,
  SimpleGrid,
  Icon,
} from "@chakra-ui/react";
import {
  SearchIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CopyIcon,
} from "@chakra-ui/icons";
import { useState, useEffect } from "react";
import axios from "axios";
import Navbar from "../../Components/Navbar/Navbar";
import Sidebar from "../../Components/Sidebar";
import Pagination from "../../Components/Common/Pagination";
import { api_url } from "../../url";

const BetHistory = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState(""); // Casino, Sports, etc.
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedPeriod, setSelectedPeriod] = useState("all_time");

  const toast = useToast();
  const SIDEBAR_WIDTH = "240px";
  const isMobile = useBreakpointValue({ base: true, md: false });

  const fetchBetHistory = async (
    pageVal = page,
    searchVal = search,
    catVal = category,
    startVal = startDate,
    endVal = endDate
  ) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${api_url}/analytics/game-transactions`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: pageVal,
          limit,
          search: searchVal,
          category: catVal === "all" ? "" : catVal,
          startDate: startVal,
          endDate: endVal,
        },
      });
      if (response.data.success) {
        setData(response.data.transactions);
        setTotalPages(response.data.pagination.pages);
        setTotalTransactions(response.data.pagination.total);
      }
    } catch (error) {
      console.error("Error fetching bet history:", error);
      toast({
        title: "Error fetching bet history.",
        description: error.response?.data?.message || "Something went wrong.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchBetHistory(page, search, category, startDate, endDate);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [page, search, category, startDate, endDate, limit]);

  const handlePeriodChange = (e) => {
    const period = e.target.value;
    setSelectedPeriod(period);

    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const d = now.getDate();

    const format = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    let start = "";
    let end = "";

    if (period === "today") {
      start = format(now);
      end = format(now);
    } else if (period === "yesterday") {
      const yday = new Date(y, m, d - 1);
      start = format(yday);
      end = format(yday);
    } else if (period === "this_week") {
      const day = now.getDay() || 7;
      const monday = new Date(y, m, d - day + 1);
      start = format(monday);
      end = format(now);
    } else if (period === "this_month") {
      start = format(new Date(y, m, 1));
      end = format(now);
    } else if (period === "all_time") {
      start = "";
      end = "";
    } else if (period === "custom") {
      return;
    }

    setStartDate(start);
    setEndDate(end);
    setPage(1);
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied!",
      status: "success",
      duration: 2000,
    });
  };

  return (
    <>
      <Navbar />
      <Sidebar />
      <Box
        p={{ base: 4, md: 8 }}
        bg="gray.50"
        minH="100vh"
        mt="64px"
        ml={{ base: 0, md: SIDEBAR_WIDTH }}
      >
        <Flex
          mb={6}
          justify="space-between"
          align={{ base: "stretch", md: "center" }}
          direction={{ base: "column", md: "row" }}
          gap={4}
        >
          <Text fontSize="2xl" fontWeight="bold" color="gray.800">
            Bet History
          </Text>
        </Flex>

        {/* Filters */}
        <Box bg="white" p={6} borderRadius="lg" mb={8} boxShadow="sm" border="1px solid" borderColor="gray.100">
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="600" color="gray.600">Quick Filter</FormLabel>
              <Select value={selectedPeriod} onChange={handlePeriodChange}>
                <option value="all_time">All Time</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="this_week">This Week</option>
                <option value="this_month">This Month</option>
                <option value="custom">Custom Range</option>
              </Select>
            </FormControl>

            {selectedPeriod === "custom" && (
              <>
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="600" color="gray.600">Start Date</FormLabel>
                  <Input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPage(1); }} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="600" color="gray.600">End Date</FormLabel>
                  <Input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPage(1); }} />
                </FormControl>
              </>
            )}

            <FormControl>
              <FormLabel fontSize="sm" fontWeight="600" color="gray.600">Game Category</FormLabel>
              <Select value={category} onChange={(e) => { setCategory(e.target.value); setPage(1); }}>
                <option value="">All Categories</option>
                <option value="casino">Casino</option>
                <option value="sports">Sports</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm" fontWeight="600" color="gray.600">Search</FormLabel>
              <InputGroup>
                <InputLeftElement pointerEvents="none"><SearchIcon color="gray.400" /></InputLeftElement>
                <Input placeholder="User, Transaction ID..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
              </InputGroup>
            </FormControl>
          </SimpleGrid>
        </Box>

        {loading ? (
          <Flex justify="center" align="center" h="200px">
            <Spinner size="xl" color="blue.500" />
          </Flex>
        ) : (
          <Box bg="white" borderRadius="lg" overflow="hidden" boxShadow="sm" border="1px solid #e2e8f0">
            <TableContainer>
              <Table variant="simple">
                <Thead bg="gray.100">
                  <Tr>
                    <Th>Transaction ID</Th>
                    <Th>Date & Time</Th>
                    <Th>User</Th>
                    <Th>Game / Description</Th>
                    <Th>Before Balance</Th>
                    <Th>Amount</Th>
                    <Th>After Balance</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {data.length > 0 ? (
                    data.map((tx) => (
                      <Tr key={tx.id} _hover={{ bg: "gray.50" }}>
                        <Td fontSize="xs" color="gray.600">
                          <Flex align="center" gap={2}>
                            {tx.id?.substring(0, 10)}...
                            <Icon as={CopyIcon} cursor="pointer" onClick={() => handleCopy(tx.id)} boxSize={3} />
                          </Flex>
                        </Td>
                        <Td fontSize="xs">{new Date(tx.dateTime).toLocaleString()}</Td>
                        <Td><Badge colorScheme="purple">{tx.user}</Badge></Td>
                        <Td>
                          <Badge
                            variant="subtle"
                            colorScheme={tx.operation?.toLowerCase().includes("win") ? "green" : "red"}
                            borderRadius="full"
                            px={3}
                          >
                            {tx.operation}
                          </Badge>
                        </Td>
                        <Td fontWeight="600">₹{tx.beforeBalance?.toFixed(2)}</Td>
                        <Td fontWeight="bold" color={tx.afterBalance > tx.beforeBalance ? "green.500" : "red.500"}>
                          {tx.afterBalance > tx.beforeBalance ? "+" : "-"}₹{tx.amount?.toFixed(2)}
                        </Td>
                        <Td fontWeight="600">₹{tx.afterBalance?.toFixed(2)}</Td>
                      </Tr>
                    ))
                  ) : (
                    <Tr>
                      <Td colSpan={7} textAlign="center" py={10} color="gray.500">No bet history found.</Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Pagination */}
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalEntries={totalTransactions}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={handleLimitChange}
        />
      </Box>
    </>
  );
};

export default BetHistory;
